/**
 * Admin Audit Sink Management API
 *
 * Requires admin session token (X-Session-Token with admin privileges).
 * All calls go to /admin/audit/sinks/* endpoints.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * List all audit sink configs for the organisation.
 * @returns {Promise<Array>} Array of sink config objects
 */
export const listAuditSinks = async () => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_LIST_AUDIT_SINKS });
    return response.data.sinks;
  } catch (error) {
    logger.error("adminAuditSinks", "Error listing sinks", { error: error.message });
    throw error;
  }
};

/**
 * Create a new audit sink. IAM dry-run is performed server-side before saving.
 * @param {"pubsub"|"bigquery"|"gcs"} sinkType
 * @param {Object} config  — sink-type-specific config fields
 * @param {boolean} [enabled=true]
 * @returns {Promise<string>} sink_id
 */
export const createAuditSink = async (sinkType, config, enabled = true) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_CREATE_AUDIT_SINK,
      data: { sink_type: sinkType, config, enabled },
    });
    return response.data.sink_id;
  } catch (error) {
    logger.error("adminAuditSinks", "Error creating sink", { error: error.message });
    throw error;
  }
};

/**
 * Enable or disable an existing sink.
 * @param {string} sinkId
 * @param {boolean} enabled
 */
export const updateAuditSink = async (sinkId, enabled) => {
  try {
    await api({
      method: "PATCH",
      url: ENDPOINTS.ADMIN_UPDATE_AUDIT_SINK(sinkId),
      data: { enabled },
    });
  } catch (error) {
    logger.error("adminAuditSinks", "Error updating sink", { error: error.message });
    throw error;
  }
};

/**
 * Permanently delete a sink config.
 * @param {string} sinkId
 */
export const deleteAuditSink = async (sinkId) => {
  try {
    await api({ method: "DELETE", url: ENDPOINTS.ADMIN_DELETE_AUDIT_SINK(sinkId) });
  } catch (error) {
    logger.error("adminAuditSinks", "Error deleting sink", { error: error.message });
    throw error;
  }
};

/**
 * Re-run the IAM dry-run on an existing sink to verify permissions are still valid.
 * @param {string} sinkId
 */
export const testAuditSink = async (sinkId) => {
  try {
    await api({ method: "POST", url: ENDPOINTS.ADMIN_TEST_AUDIT_SINK(sinkId) });
  } catch (error) {
    logger.error("adminAuditSinks", "Error testing sink", { error: error.message });
    throw error;
  }
};

/**
 * Manually flush the GCS buffer for a gcs-type sink.
 * @param {string} sinkId
 * @returns {Promise<number>} Number of events written
 */
export const flushGCSSink = async (sinkId) => {
  try {
    const response = await api({ method: "POST", url: ENDPOINTS.ADMIN_FLUSH_AUDIT_SINK(sinkId) });
    return response.data.events_written;
  } catch (error) {
    logger.error("adminAuditSinks", "Error flushing GCS sink", { error: error.message });
    throw error;
  }
};
